import { FaCalendarAlt, FaTag, FaSearch, FaTags, FaNewspaper } from 'react-icons/fa';
import styles from './Insights.module.scss';
import aiTrendsImage from '../../assets/AI-Trends-2025.jpg';
import serviceNowImage from '../../assets/serviceNowwebside.jpg';
import govImage from '../../assets/gob imple.jpg';
import heroImage from '../../assets/b21-hero.webp';
import NewsletterForm from '../../components/Newsletterform';
import { useState, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Seo, { seoPropsFor } from '../../components/Seo/Seo';
import PageHero from '../../components/PageHero/PageHero';
import { PAGE_SEO } from '../../content/site';

type BlogPost = {
  postID?: string;
  pubDate?: string;
  body?: string;
  category?: string;
  description?: string;
  imageKey?: string;
  imageUrl?: string;
  link?: string;
  title?: string;
  active?: boolean;
}

const InsightsPage = () => {
  const [isVisible, setIsVisible] = useState(false);

  // The scroll cue and its handler moved into PageHero, which now owns both
  // for every page that has one.

  // search state
  const [searchQuery, setSearchQuery] = useState('');

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);

  // BlogPosts API integration
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [blogPostsLoading, setBlogPostsLoading] = useState(false);
  const [blogPostsError, setBlogPostsError] = useState<string | null>(null);

  // CRA env var
  const apiBase = useMemo(() => {
    const raw = process.env.REACT_APP_BLOGPOSTS_API || '';
    return raw.trim().replace(/\/$/, '');
  }, []);

  // Load blog posts from API
  useEffect(() => {
    if (!apiBase) {
      setBlogPostsError('Missing environment variable: REACT_APP_BLOGPOSTS_API');
      return;
    }

    const controller = new AbortController();

    const loadBlogPosts = async () => {
      try {
        setBlogPostsLoading(true);
        setBlogPostsError(null);

        const url = `${apiBase}/blogposts`;
        const res = await fetch(url, {
          method: `GET`,
          signal: controller.signal
        });

        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new Error(`BlogPosts API error (${res.status}): ${text || res.statusText}`);
        }

        const data = await res.json();
        const normalized =
          data?.body && typeof data.body === 'string'
            ? JSON.parse(data.body)
            : data;
      
        const items: BlogPost[] = Array.isArray(normalized) ? normalized : [];
        const activeOnly = items.some(i => typeof i.active === 'boolean')
          ? items.filter(i => i.active)
          : items;

        setBlogPosts(activeOnly);
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        console.error('Failed to load blog posts:', err);
        setBlogPostsError(err?.message || 'Failed to load blog posts');
      } finally {
        setBlogPostsLoading(false);
      }
  };

loadBlogPosts();
return () => controller.abort();
}, [apiBase]);

  useEffect(() => {
    // Trigger the animation when component mounts
    setIsVisible(true);
  }, []);

  // post view use effect
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedPost(null);
    };
    if (selectedPost) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', onKeyDown);
    }
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [selectedPost]);

  const categories = useMemo(() => {
    const cats = new Set<string>();
    blogPosts.forEach((post) => {
      const cat = post.category?.trim();
      if (cat) {
        cats.add(cat);
      }
    });
    return Array.from(cats).sort((a, b) => a.localeCompare(b));
  }, [blogPosts]);

  // helpers

  const fallbackImages: Record<string, string> = {
    'AI-Trends-2025.jpg': aiTrendsImage,
    'serviceNowwebside.jpg': serviceNowImage,
    'gob-imple.jpg': govImage
  };

  const isAbsoluteUrl = (v: string) => /^https?:\/\//i.test(v);

  const normalizeKey = (key: string) => {
    const trimmed = key.trim().replace(/^\/+/, '');
    if (trimmed.includes('/')) return trimmed;
    return `images/${trimmed}`;
  };

  // for loading image of blog posts
  const buildImageUrl = (post: BlogPost) => {
    const bucket = (process.env.REACT_APP_S3_BUCKET || '').trim();
    const region  = (process.env.REACT_APP_S3_REGION || '').trim();
    const s3base = bucket && region ? `https://${bucket}.s3.${region}.amazonaws.com` : '';
    
    if (post.imageKey) {
      const key = normalizeKey(post.imageKey);
      // encode each path segment so keys with spaces/special chars work
      const encodedKey = key
        .split('/')
        .map((s) => encodeURIComponent(s))
        .join('/');

      const url = s3base ? `${s3base}/${encodedKey}` : '';
      console.debug('Image URL from imageKey:', url, { imageKey: post.imageKey, other: post });
      return url;
    }
    if (post.imageUrl) {
      const raw = post.imageUrl.trim();
      
      if (isAbsoluteUrl(raw)) {
        console.debug('Image URL absolute:', raw, { post });
        return raw;
      }

      if (raw.startsWith('/')) {
        console.debug('Image URL root-relative:', raw, { post });
        return raw;
      }

      if (s3base) {
        const encoded = raw
          .split('/')
          .map((s) => encodeURIComponent(s))
          .join('/');
        const url = `${s3base}/${encoded}`;
        console.debug('Image URL from relative imageUrl -> S3:', url, { raw, post });
        return url;
      }
      
      // local fallback
      const local = fallbackImages[raw];
      if (local) {
        console.debug('Image URL local fallback:', local, {raw, post });
        return local;
      }
    }
    return '';
  };

  // searchable list of posts
  const filteredPosts = useMemo(() => {
    let posts = blogPosts;
 
   posts = [...posts].sort(
     (a, b) =>
       new Date(b.pubDate || '').getTime() -
       new Date(a.pubDate || '').getTime()
   );
    
    const q = searchQuery.trim().toLowerCase();
  return posts.filter((post) => {
    const searchableText = [
      post.title,
      post.description,
      post.category,
      post.body,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    const matchesSearch = q ? searchableText.includes(q) : true;

    const matchesCategory = selectedCategory
      ? (post.category || '').trim().toLowerCase() === selectedCategory.trim().toLowerCase()
      : true;

      return matchesSearch && matchesCategory;
  })
  }, [blogPosts, searchQuery, selectedCategory]);

  // helper: date formatting
  const formatDateOnly = (v?: string) => {
    if (!v) return '';
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return v;
    return d.toISOString().slice(0, 10);
  };

  return (
    <div className={styles.blogPage}>
      <Seo {...seoPropsFor(PAGE_SEO.insights)} />

      <div className={isVisible ? 'main-content' : ''}>
        <PageHero
          title="BizSolutions Insights"
          subtitle="Get the latest updates on AI, ServiceNow, and innovation trends"
          image={heroImage}
          tone="insights"
          showScrollHint
        />

        {/* Blog Content Section */}
        <section className={styles.blogContent}>
          <div className={styles.container}>
            <div className={styles.blogHeader}>
              <h2>Latest Insights</h2>
            </div>
            <div className={styles.blogGrid}>
              <div className={styles.blogPosts}>
                {blogPostsLoading && <p>Loading blog posts...</p>}
                {blogPostsError && <p className={styles.error}>{blogPostsError}</p>}
                {!blogPostsLoading && !blogPostsError && filteredPosts.length === 0 && (<p>No blog posts match your search.</p>)}
                {!blogPostsLoading && !blogPostsError && filteredPosts.map((post, index) => (
                  <button
                    key={post.postID || post.title}
                    type="button"
                    className={styles.blogPostCard}
                    onClick={() => setSelectedPost(post)}
                    aria-label={`Open article: ${post.title}`}
                  >
                    <div className={styles.postImage}>
                      <img
                        src={buildImageUrl(post)}
                        alt={post.title || 'Blog post image'}
                        className={styles.cardImage}
                        onError={(e) => {
                          console.error('Image failed:', {
                            post,
                            attemptedSrc: (e.currentTarget as HTMLImageElement).src
                          });
                        }}
                        />
                    </div>
                    
                    <div className={styles.cardOverlay}>
                      <div className={styles.postMeta}>
                        <span className={styles.postDate}>
                          <FaCalendarAlt className={styles.icon} /> {formatDateOnly(post.pubDate)}
                        </span>
                        <span className={styles.postCategory}>
                          <FaTag className={styles.icon} /> {post.category}
                        </span>
                      </div>
                      
                      <h2>{post.title}</h2>
                      <p>{post.description}</p>
                      </div>
                  </button>
                ))}
              </div>

              {/* Blog Sidebar */}
              <aside className={styles.blogSidebar}>
                {/* Search Widget */}
                <div className={styles.sidebarWidget}>
                  <h3><FaSearch className={styles.icon} /> Search Insights</h3>
                  <form
                    className={styles.searchForm}
                    onSubmit={(e) => e.preventDefault()}
                  >
                    <input
                      type="text"
                      placeholder="Search articles..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.currentTarget.value);
                        setSelectedCategory(null);
                      }}
                    />
                    <button type="submit" aria-label="Search insights">
                      <FaSearch />
                    </button>
                  </form>
                </div>

                {/* Categories Widget */}
                <div className={styles.sidebarWidget}>
                  <h3><FaTags className={styles.icon} /> Categories</h3>
                  <ul className={styles.categoriesList}>
                    {categories.map((cat) => (
                      <li key = {cat}>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setSelectedCategory(selectedCategory === cat ? null : cat);
                          }}
                          style={{
                            fontWeight: selectedCategory === cat ? 'bold' : 'normal',
                            color: selectedCategory === cat ? '#ff8c00' : undefined
                          }}
                        >
                          {cat}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Newsletter Widget */}
                <div className={styles.sidebarWidget}>
                  <h3><FaNewspaper className={styles.icon} /> Newsletter</h3>
                  <p>Subscribe to get our latest articles and updates</p>
                  <div className={styles.newsletterForm}>
                    <NewsletterForm />
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </div>

      {/* Modal for Post */}
      {selectedPost && (
      <div
        className={styles.modalOverlay}
        role="dialog"
        aria-modal="true"
        aria-labelledby="post-modal-title"
        onClick={() => setSelectedPost(null)}
      >
        <div
          className={styles.modalPanel}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.modalHeader}>
            <h2 id="post-modal-title">{selectedPost?.title}</h2>
            <button
              type="button"
              className={styles.modalClose}
              onClick={() => setSelectedPost(null)}
              aria-label="Close"
            >
              X
            </button>
          </div>

          <div className={styles.modalMeta}>
            <span><FaCalendarAlt className={styles.icon} /> {formatDateOnly(selectedPost?.pubDate)}</span>
            <span><FaTag className={styles.icon} /> {selectedPost?.category}</span>
          </div>

          <div className={styles.markdownBody}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {selectedPost?.body || ''}
            </ReactMarkdown>
          </div>
        </div>
      </div>
      )}

      
    </div>
  );
};

export default InsightsPage;
